// Change-premium (变更补缴) calculation.
//
// Per logic/CALCULATION_CHANGE_PREMIUM.md §4: run the new-supply MVP
// calculation on the before and after snapshots, take the difference,
// clamp to [0, ∞) for the payable premium. If the difference is
// negative, surface a note.
//
// Per CLAUDE.md §3 and the project rules, retained-land and urban-renewal
// projects are NOT fully calculated here. We emit a warning into both
// `beforeResult` and `afterResult` so the UI sees it regardless of which
// result it inspects, but the numeric results still come from the
// shared MVP path (otherwise we would be pretending to fully calculate
// them).

import type {
  ChangePremiumInput,
  ChangePremiumResult,
} from "../types/change-premium.js";
import type { CalculationResult } from "../types/calculation-result.js";
import type { ProjectType } from "../types/enums.js";
import type { Parcel } from "../types/parcel.js";
import type { ValidationMessage } from "../types/validation.js";

import { calculateNewSupplyLandPrice } from "./new-supply.js";

const NEGATIVE_DIFF_NOTE = "差额为负，不再计收地价。";

const RETAINED_LAND_NOTE =
  "该情形涉及留用土地项目修正系数和可能的分段计算，请进入留用土地模块或人工复核。";

const URBAN_RENEWAL_NOTE =
  "该情形涉及拆除重建类城市更新项目专项规则，请进入城市更新模块或人工复核。";

const RETAINED_LAND_PROJECT_TYPES: ReadonlySet<ProjectType> = new Set([
  "留用土地",
]);

const URBAN_RENEWAL_PROJECT_TYPES: ReadonlySet<ProjectType> = new Set([
  "城市更新",
]);

/**
 * Compute the payable change premium between two parcel snapshots.
 *
 * The "before" and "after" snapshots are each fully calculated by the
 * MVP new-supply path; we then take the delta. Negative deltas produce
 * `premium = 0` and a `note`. Retained-land and urban-renewal project
 * types do not short-circuit the calculation — instead a warning is
 * appended to both `validationMessages` lists, and the numeric results
 * are returned unchanged so the UI can choose how to display them.
 */
export function calculateChangePremium(
  input: ChangePremiumInput,
): ChangePremiumResult {
  const beforeResult = calculateNewSupplyLandPrice({
    parcel: input.parcelBefore,
    prices: input.prices,
    items: input.itemsBefore,
  });
  const afterResult = calculateNewSupplyLandPrice({
    parcel: input.parcelAfter,
    prices: input.prices,
    items: input.itemsAfter,
  });

  const beforeWithWarnings = appendParcelWarnings(
    beforeResult,
    input.parcelBefore,
  );
  const afterWithWarnings = appendParcelWarnings(
    afterResult,
    input.parcelAfter,
  );

  const difference =
    afterWithWarnings.totalLandPrice - beforeWithWarnings.totalLandPrice;
  const premium = Math.max(difference, 0);
  const note = difference < 0 ? NEGATIVE_DIFF_NOTE : undefined;

  return {
    beforeResult: beforeWithWarnings,
    afterResult: afterWithWarnings,
    difference,
    premium,
    note,
  };
}

/**
 * If a parcel's project type is one we know we are not fully supporting
 * for change-premium, append a warning so the UI can flag it. We
 * intentionally do NOT change the numeric result — that would be
 * pretending to handle the case.
 */
function appendParcelWarnings(
  result: CalculationResult,
  parcel: Parcel,
): CalculationResult {
  const extra = specialProjectTypeWarnings(parcel);
  if (extra.length === 0) return result;
  return {
    ...result,
    validationMessages: [...result.validationMessages, ...extra],
  };
}

function specialProjectTypeWarnings(parcel: Parcel): ValidationMessage[] {
  const messages: ValidationMessage[] = [];
  if (RETAINED_LAND_PROJECT_TYPES.has(parcel.projectType)) {
    messages.push({
      level: "warning",
      field: "projectType",
      message: RETAINED_LAND_NOTE,
      suggestion: "MVP 不自动测算留用土地变更补缴，请进入留用土地模块或人工复核。",
    });
  }
  if (URBAN_RENEWAL_PROJECT_TYPES.has(parcel.projectType)) {
    messages.push({
      level: "warning",
      field: "projectType",
      message: URBAN_RENEWAL_NOTE,
      suggestion: "MVP 不自动测算城市更新变更补缴，请进入城市更新模块或人工复核。",
    });
  }
  return messages;
}

// Re-exported for tests / external callers that want to detect
// special project types without re-implementing the lookup.
export { RETAINED_LAND_PROJECT_TYPES, URBAN_RENEWAL_PROJECT_TYPES };
